import axios, { AxiosResponse, CancelToken } from 'axios'
import { useAnalyticsFeatureFlag } from '../contexts/AnalyticsFeatureFlagContext'
import { ChartEntry } from '../data/model/chart'
import { SelectedFilter, WeightingScheme } from '../data/model/results'
import {
  RehydrationInProgressError,
  parseRetryAfter
} from '../utils/RehydrationInProgressError'
import { buildRequestUrl, createApiConfig } from '../utils/apiUtils'
import requestWithRetry from '../utils/requestWithRetry'
import { toResponseFilters } from '../utils/resultsUtils'
import { usePollOn202 } from './usePollOn202'

const SUMMARY_ENDPOINT = `${
  process.env.REACT_APP_URL_SURVEY_RESULTS || '/v2/survey-results'
}/api/v1.0/summary`

const ANALYTICS_SUMMARY_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/analytics/survey`

// Combined (multi-market) results are keyed on the project, not a survey — the
// backend resolves the project to its market surveys, pools the counts and
// prepends a synthetic market question. There is no legacy equivalent, so this
// always targets the core-responses analytics API.
const ANALYTICS_COMBINED_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/analytics/surveys/results`

interface SummaryData {
  data: ChartEntry[]
  weightingScheme: WeightingScheme | undefined
  error: boolean
  loading: boolean
}

interface SummaryApiData {
  results: ChartEntry[]
  weightingScheme: WeightingScheme
  totalChartsCount: number | null
}

const useSummaryData = (
  surveyId: string,
  offset?: number,
  limit?: number,
  filters?: SelectedFilter[],
  language?: string
): SummaryData => {
  const { useAnalyticsAPI, isLoading: isFeatureFlagLoading } =
    useAnalyticsFeatureFlag()

  const { status, data } = usePollOn202<SummaryApiData>(
    (signal) => {
      const source = axios.CancelToken.source()
      if (signal.aborted) source.cancel()
      else
        signal.addEventListener('abort', () => source.cancel(), { once: true })
      return getSummaryData(
        surveyId,
        offset,
        limit,
        filters,
        source.token,
        useAnalyticsAPI,
        language
      )
    },
    [
      surveyId,
      offset,
      limit,
      filters,
      useAnalyticsAPI,
      isFeatureFlagLoading,
      language
    ],
    { enabled: !isFeatureFlagLoading && useAnalyticsAPI !== null }
  )

  return {
    data: data?.results ?? [],
    weightingScheme: data?.weightingScheme,
    error: status === 'error',
    loading: status === 'loading' || status === 'rehydrating'
  }
}

export default useSummaryData

const DEFAULT_PAGE_SIZE = 50

/**
 * Fetches all summary data by handling pagination automatically.
 * Makes an initial request to get the total count, then fetches remaining pages in parallel.
 */
export const getAllSummaryData = async (
  surveyId: string,
  filters?: SelectedFilter[],
  cancelToken?: CancelToken,
  useAnalyticsAPI: boolean | null = false,
  language?: string
): Promise<
  { results: ChartEntry[]; weightingScheme: WeightingScheme } | undefined
> => {
  // RehydrationInProgressError from any page (first or subsequent parallel
  // fetches) is treated as "no data yet" so export-feature callers (Download
  // chart data, Export to PPTX) don't see an uncaught throw — they just get
  // the same empty-data outcome they had before 202 handling existed.
  try {
    const firstPageData = await getSummaryData(
      surveyId,
      0,
      DEFAULT_PAGE_SIZE,
      filters,
      cancelToken,
      useAnalyticsAPI,
      language
    )

    if (!firstPageData) {
      return undefined
    }

    const {
      results: firstPageResults,
      weightingScheme,
      totalChartsCount
    } = firstPageData

    // If no totalChartsCount or all results fit in first page, return immediately
    if (!totalChartsCount || totalChartsCount <= DEFAULT_PAGE_SIZE) {
      return { results: firstPageResults, weightingScheme }
    }

    // Calculate remaining pages needed
    const remainingCount = totalChartsCount - DEFAULT_PAGE_SIZE
    const additionalPagesCount = Math.ceil(remainingCount / DEFAULT_PAGE_SIZE)

    // Fetch remaining pages in parallel
    const additionalPagePromises: Promise<
      | {
          results: ChartEntry[]
          weightingScheme: WeightingScheme
          totalChartsCount: number | null
        }
      | undefined
      | void
    >[] = []

    for (let i = 1; i <= additionalPagesCount; i++) {
      const offset = i * DEFAULT_PAGE_SIZE
      additionalPagePromises.push(
        getSummaryData(
          surveyId,
          offset,
          DEFAULT_PAGE_SIZE,
          filters,
          cancelToken,
          useAnalyticsAPI,
          language
        )
      )
    }

    const additionalPages = await Promise.all(additionalPagePromises)

    // Combine all results
    const allResults = [...firstPageResults]
    for (const pageData of additionalPages) {
      if (pageData?.results) {
        allResults.push(...pageData.results)
      }
    }

    return { results: allResults, weightingScheme }
  } catch (error) {
    if (error instanceof RehydrationInProgressError) return undefined
    throw error
  }
}

/**
 * Fetches combined (multi-market) results pooled across a project's market
 * surveys from fd-core-responses. Mirrors getSummaryData but is keyed on the
 * project (POST /analytics/surveys/results with a projectId body) rather than a
 * single survey, and always uses the core-responses analytics API — there is no
 * legacy equivalent, so there is no useAnalyticsAPI toggle. The response shape
 * is identical to the single-survey endpoint, with a synthetic market question
 * prepended at chart index 0.
 */
export const getCombinedSummaryData = async (
  projectId: string,
  offset?: number,
  limit?: number,
  filters?: SelectedFilter[],
  cancelToken?: CancelToken,
  language?: string
) => {
  // Narrowed to the API's own fields, and merged by questionId: the combined
  // panel's selections also carry marketLabel, which disambiguates market-split
  // cards in the UI and would otherwise leave two same-question entries the
  // endpoint reads as an impossible AND. See toResponseFilters.
  const responseFilters = toResponseFilters(filters)
  const forkFilters =
    filters
      ?.filter(
        (filter) =>
          filter.forkId &&
          filter.branchNumbers &&
          filter.branchNumbers.length > 0
      )
      .map((fl) => {
        return {
          ...fl,
          branchNumbers: fl.branchNumbers?.map((br) => Number.parseInt(br, 10))
        }
      }) || []
  const postPayload = {
    projectId,
    responseFilters,
    forkFilters,
    offset,
    limit,
    // Omitted rather than sent empty: the API rejects anything that isn't a
    // BCP-47 code, and leaving it out keeps the existing resolution behaviour.
    ...(language ? { language } : {})
  }

  const requestUrl = buildRequestUrl(
    ANALYTICS_COMBINED_ENDPOINT,
    process.env.REACT_APP_REST_DOMAIN
  )

  return requestWithRetry({
    method: 'post',
    url: requestUrl,
    payload: postPayload,
    getConfig: () => createApiConfig(cancelToken)
  })
    .then((response: AxiosResponse<SummaryApiData>) => response.data)
    .catch((error) => {
      if (axios.isCancel(error)) return undefined
      throw new Error('Error fetching combined summary data')
    })
}

export const getSummaryData = async (
  surveyId: string,
  offset?: number,
  limit?: number,
  filters?: SelectedFilter[],
  cancelToken?: CancelToken,
  useAnalyticsAPI: boolean | null = false,
  language?: string
) => {
  const responseFilters = filters?.filter((filter) => !!filter.questionId) || []
  const forkFilters =
    filters
      ?.filter(
        (filter) =>
          filter.forkId &&
          filter.branchNumbers &&
          filter.branchNumbers.length > 0
      )
      .map((fl) => {
        return {
          ...fl,
          branchNumbers: fl.branchNumbers?.map((br) => Number.parseInt(br, 10))
        }
      }) || []
  const legacyRequestUrl = buildRequestUrl(
    SUMMARY_ENDPOINT,
    process.env.REACT_APP_REST_DOMAIN
  )

  const analyticsRequestUrl = buildRequestUrl(
    `${ANALYTICS_SUMMARY_ENDPOINT}/${surveyId}/results`,
    process.env.REACT_APP_REST_DOMAIN
  )

  // Use the flag value directly - never fall back to localStorage
  // If null, use legacy API (will only happen if called incorrectly)
  const shouldUseAnalyticsAPI = useAnalyticsAPI === true

  const postPayload = {
    surveyId,
    responseFilters,
    forkFilters,
    offset,
    limit,
    // Only fd-core-responses resolves question text per language; the legacy
    // service has no such field, so it is left off that request entirely.
    ...(shouldUseAnalyticsAPI && language ? { language } : {})
  }

  return requestWithRetry({
    method: 'post',
    url: shouldUseAnalyticsAPI ? analyticsRequestUrl : legacyRequestUrl,
    payload: postPayload,
    getConfig: () => createApiConfig(cancelToken)
  })
    .then((response: AxiosResponse<SummaryApiData>) => {
      // 202 Accepted from fd-core-responses signals that the survey's data
      // is still being rehydrated from BigQuery. Throw a typed error so the
      // caller can schedule the next poll at the cadence indicated by
      // Retry-After. axios treats 2xx as success, so this branch wouldn't
      // fire from the surrounding .catch.
      if (response.status === 202) {
        throw new RehydrationInProgressError(
          parseRetryAfter(response.headers['retry-after'])
        )
      }
      return response.data
    })
    .catch((error) => {
      if (axios.isCancel(error)) return undefined
      if (error instanceof RehydrationInProgressError) throw error
      throw new Error('Error fetching summary data')
    })
}
